import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import api from '../api/axios';

const Login = () => {
    const [cacheBuster] = useState(() => Date.now());
    const getCleanImageUrl = (url) => {
        if (!url) return '';
        return `${url}${url.includes('?') ? '&' : '?'}v=${cacheBuster}`;
    };

    const { login, user } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const [loginInput, setLoginInput] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [viewMode, setViewMode] = useState('landing');
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        if (user) {
            navigate('/');
        }
    }, [user, navigate]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        const cleanInput = (loginInput || '').trim();
        const result = await login(cleanInput);
        if (result.success) {
            try {
                await api.post('/sessions/log', {
                    page_name: 'login',
                    action_type: 'login'
                });
            } catch (err) {
                console.error("Failed to log login action", err);
            }
            navigate('/');
        } else {
            setError('Valide su información, si no se encuentra registrado, lo invitamos a registrarse.');
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0a58ca] rounded-full animate-spin"></div>
            </div>
        );
    }

    const pageDesign = config?.pageDesigns?.login || {};
    const primaryColor = '#c20042';
    const secondaryColor = '#810691';

    const logoUrl = '/assets/cardio/CS_Logo.png';
    const bgImage = isMobile ? '/assets/cardio/CS_Fondo-vertical.png' : '/assets/cardio/CS_Fondo-Horizontal.png';

    const title = 'ACCESO';
    const subtitle = 'Ingrese sus datos';

    const containerStyle = {
        backgroundColor: '#550043',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        minHeight: '100vh',
    };

    return (
        <div
            style={containerStyle}
            className="min-h-screen flex flex-col justify-between items-center font-sans relative overflow-x-hidden"
        >
            {/* Logo oficial Cardio Summit 2026 */}
            <div className="relative z-50 w-full flex flex-col items-center pt-8 pb-4 flex-shrink-0">
                <img
                    src={logoUrl}
                    alt="Cardio Summit 2026"
                    className="w-[280px] sm:w-[340px] h-auto object-contain drop-shadow-xl"
                />
            </div>

            {/* Content Area */}
            <div className="w-full max-w-sm px-6 z-10 flex flex-col justify-center flex-grow mt-4 mb-8 animate-fadeIn">

                {/* Página 1: Landing con botones INICIAR y REGISTRO */}
                {viewMode === 'landing' && (
                    <div className="flex flex-col gap-6 w-full max-w-[320px] mx-auto">
                        <button
                            onClick={() => setViewMode('login')}
                            style={{ backgroundColor: '#c20042' }}
                            className="w-full text-white py-4 px-6 rounded-2xl font-black uppercase tracking-widest text-[16px] sm:text-[18px] shadow-[0_10px_25px_rgba(194,0,66,0.5)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center"
                        >
                            INICIAR
                        </button>

                        <button
                            onClick={() => navigate('/register')}
                            style={{ backgroundColor: '#810691' }}
                            className="w-full text-white py-4 px-6 rounded-2xl font-black uppercase tracking-widest text-[16px] sm:text-[18px] shadow-[0_10px_25px_rgba(129,6,145,0.5)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center"
                        >
                            REGISTRO
                        </button>
                    </div>
                )}

                {/* Página 2: Tarjeta ACCESO */}
                {viewMode === 'login' && (
                    <div
                        style={{
                            borderColor: '#c20042',
                            backgroundColor: 'rgba(85, 0, 67, 0.75)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)'
                        }}
                        className="w-full p-8 rounded-[2.5rem] border-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)] transition-all duration-500"
                    >
                        <div className="text-center mb-6">
                            <h3 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-wider">{title}</h3>
                            <p className="text-sm font-semibold text-white/90 tracking-wide mt-1">{subtitle}</p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2 text-left">
                                <label
                                    style={{ color: '#c20042' }}
                                    className="text-xs uppercase font-bold tracking-wider pl-1 block"
                                >
                                    {config?.registration_fields?.identification === false ? 'Correo Electrónico' : 'Identificación'}
                                </label>
                                <input
                                    className="w-full bg-white border border-transparent p-4 rounded-xl focus:outline-none transition-all font-bold text-slate-800 text-left shadow-inner placeholder:text-slate-400"
                                    placeholder={config?.registration_fields?.identification === false ? 'tu@email.com' : 'Ejemplo: 123456'}
                                    value={loginInput}
                                    onChange={(e) => setLoginInput(e.target.value)}
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                style={{ backgroundColor: '#c20042' }}
                                className="w-full text-white py-4 rounded-2xl font-black uppercase tracking-widest text-[16px] shadow-[0_8px_20px_rgba(194,0,66,0.4)] hover:scale-[1.02] active:scale-95 transition-all duration-300"
                            >
                                {isLoading ? 'Accediendo...' : 'Ingresar'}
                            </button>

                            {error && (
                                <div className="space-y-3 mt-4">
                                    <div className="bg-red-950/80 text-red-200 p-3 rounded-xl text-[11px] font-bold tracking-wide border border-red-500/30 text-center">
                                        {error}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/register')}
                                        style={{ backgroundColor: '#810691' }}
                                        className="w-full text-white py-3.5 px-6 rounded-2xl font-black uppercase tracking-widest text-[14px] shadow-lg hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center"
                                    >
                                        REGISTRARSE
                                    </button>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => setViewMode('landing')}
                                className="w-full text-white/70 text-xs font-bold uppercase tracking-wider hover:text-white transition-colors pt-2 block text-center"
                            >
                                ← Volver
                            </button>
                        </form>
                    </div>
                )}
            </div>

            {/* Footer legal con avales y logo Adium idéntico al PDF */}
            <footer className="w-full px-4 sm:px-6 py-6 flex-shrink-0 flex flex-col items-center gap-6 z-40 select-none bg-transparent max-w-4xl mx-auto">
                {/* Tarjeta de Avales de Asociaciones Médicas idéntica al diseño */}
                <div className="w-full flex justify-center">
                    <img
                        src="/assets/cardio/Logos_Asociaciones.png"
                        alt="CardioSummit 2026, un evento con el aval de: SCC, ASOCOLNEF, ACE"
                        className="w-full max-w-3xl h-auto object-contain drop-shadow-xl rounded-[28px] pointer-events-auto"
                    />
                </div>

                <div className="w-full flex items-center justify-between gap-4">
                    <div className="flex flex-col items-center flex-shrink-0">
                        <img
                            src="/assets/cardio/CS_LogoAdium.png"
                            alt="Adium"
                            className="h-10 sm:h-12 w-auto object-contain drop-shadow"
                        />
                        <span className="text-[11px] font-bold text-white tracking-wider mt-1">Adium</span>
                    </div>
                    <div className="flex-1 text-[8px] sm:text-[9px] text-white/80 leading-snug text-left pl-2">
                        Este evento está dirigido exclusivamente al cuerpo médico y es de carácter personal e intransferible. Adium no promociona ni promueve el uso de sus productos / medicamentos en forma diferente al aprobado por la autoridad regulatoria e incluida en la información de prescripción o ficha técnica. Para mayor información comunicarse con el departamento médico de Adium S.A.S. Carrera 16 No. 85-96. Bogotá D.C. - Teléfono: +601 6460505. Si conoce un evento adverso de nuestros medicamentos, por favor reportarlo a farmacovigilancia@adium.com.co. CO--2600409
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Login;
